import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  IconButton,
  MenuItem,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from "@mui/material";
import {
  AddAutocomplete,
  AddButton,
  ControlsContainer,
  Header,
  MembersTableRow,
  ScrollableTable,
  ScrollableTableContainer,
  TableCellNoPadding
} from "components/Settings/RolesSectionSharedStyles";
import DeleteIcon from "@mui/icons-material/Delete";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { Dropdown } from "components/Dropdown";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useLoadCounter } from "hooks/useLoadCounter";

const OWNER = "Owner";
const MEMBER = "Member";

export function RoleMembersSection({
  canEditRoleUsers,
  updateRole,
  role,
  users,
  isSaved,
  loading,
  error
}) {
  const tableLoadCount = useLoadCounter(loading);
  const [addedUser, setAddedUser] = useState(null);
  const [unsavedUsers, setUnsavedUsers] = useState([]);

  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  useEffect(() => {
    if (isSaved) {
      setUnsavedUsers([]);
    }
  }, [isSaved]);

  const mappedUsers =
    role?.users?.map(id => users.find(user => user.id === id)) ?? [];

  const getUserName = id => {
    const user = users?.find(user => user.id === id);
    return `${user.first_name} ${user.last_name}`;
  };

  const deleteUserFromRole = id => {
    const userName = getUserName(id);

    if (!canEditRoleUsers) {
      addSnackbarToQueue({
        severity: "error",
        message: `Can not remove ${userName} from ${role?.name} as you do not have required permission.`
      });
      return;
    }

    if (role?.users && role?.owners) {
      const owners = role.owners.filter(userId => userId !== id);

      if (role.owners.length > 0 && owners.length === 0) {
        addSnackbarToQueue({
          severity: "error",
          message: `Can not remove ${userName} from ${role?.name} as it needs an owner.`
        });
        return;
      }

      const updatedUsers = role.users.filter(userId => userId !== id);
      updateRole({ owners, users: updatedUsers });
      setUnsavedUsers(usersUnsaved =>
        usersUnsaved.filter(unsavedUserId => unsavedUserId !== id)
      );
    }
  };

  const addUserToRole = id => {
    const userName = getUserName(id);

    if (!canEditRoleUsers) {
      addSnackbarToQueue({
        severity: "error",
        message: `Can not add ${userName} to ${role?.name} as you do not have required permission.`
      });
      return;
    }

    if (role?.users) {
      const updatedUsers = !role.users.includes(id)
        ? [...role.users, id]
        : role.users;
      updateRole({ users: updatedUsers });
      setUnsavedUsers(usersUnsaved => [...new Set([...usersUnsaved, id])]);
      setAddedUser(null);
    }
  };

  const changeUserRole = (newRole, id) => {
    const userName = getUserName(id);

    if (!canEditRoleUsers) {
      addSnackbarToQueue({
        severity: "error",
        message: `Can not change ${userName} role type for ${role?.name} as you do not have required permission.`
      });
      return;
    }

    if (role?.owners && role?.users) {
      let owners = role.owners.filter(userId => userId !== id);
      const updatedUsers = !role.users.includes(id)
        ? [...role.users, id]
        : role.users;

      if (owners.length === 0) {
        addSnackbarToQueue({
          severity: "error",
          message: `Can not make ${userName} a member of ${role?.name} as it needs an owner.`
        });
        return;
      }

      if (newRole === OWNER) {
        owners = !role.owners.includes(id) ? [...role.owners, id] : role.owners;
      }
      updateRole({ owners: owners, users: updatedUsers });
    }
  };

  return (
    <>
      <Header>Members</Header>
      <ControlsContainer>
        <AddAutocomplete
          label="Add User"
          size="small"
          autoHighlight
          autoComplete
          disabled={!canEditRoleUsers}
          aria-label="add user autocomplete"
          options={users?.filter(({ id }) => !role?.users.includes(id)) ?? []}
          getOptionLabel={option =>
            `${option?.first_name} ${option?.last_name}`
          }
          value={addedUser}
          onChange={(_, value) => setAddedUser(value)}
        />
        <AddButton
          variant="contained"
          color="primary"
          disabled={!canEditRoleUsers || !addedUser}
          onClick={() => addUserToRole(addedUser.id)}
        >
          Add User
        </AddButton>
      </ControlsContainer>
      <ScrollableTableContainer component={Paper}>
        <ProgressIndicatorContainer loading={loading} error={error}>
          <PerfectScrollbar>
            <ScrollableTable stickyHeader aria-label="role members table">
              <TableHead>
                <TableRow>
                  <TableCell data-col={1}>ID</TableCell>
                  <TableCell data-col={2}>Name</TableCell>
                  <TableCell data-col={3}>E-Mail</TableCell>
                  <TableCell data-col={4}>Type</TableCell>
                  <TableCell data-col={5}>Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                data-qa_loading={loading}
                data-qa_load_count={tableLoadCount}
              >
                {mappedUsers.map(user =>
                  user ? (
                    <MembersTableRow
                      key={user.id}
                      aria-label={`user row ${user.id}`}
                      $isSaved={unsavedUsers.includes(user.id)}
                    >
                      <TableCell data-col={1}>{user.id}</TableCell>
                      <TableCell data-col={2}>
                        <Link
                          to={`/pages/settings/user/${user.id}`}
                        >{`${user.first_name} ${user.last_name}`}</Link>
                      </TableCell>
                      <TableCell data-col={3}>{user.email}</TableCell>
                      <TableCellNoPadding data-col={4}>
                        <Tooltip
                          title={
                            role.builtin
                              ? "Cannot change member type on built-in roles"
                              : ""
                          }
                        >
                          <Dropdown
                            size="small"
                            required
                            disabled={!canEditRoleUsers || role.builtin}
                            onChange={event =>
                              changeUserRole(event.target.value, user.id)
                            }
                            value={
                              role?.owners?.includes(user.id) ? OWNER : MEMBER
                            }
                          >
                            <MenuItem value={OWNER}>{OWNER}</MenuItem>
                            <MenuItem value={MEMBER}>{MEMBER}</MenuItem>
                          </Dropdown>
                        </Tooltip>
                      </TableCellNoPadding>
                      <TableCell data-col={5}>
                        <IconButton
                          aria-label={`delete from role ${user.id}`}
                          disabled={!canEditRoleUsers}
                          onClick={() => deleteUserFromRole(user.id)}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </MembersTableRow>
                  ) : null
                )}
              </TableBody>
            </ScrollableTable>
          </PerfectScrollbar>
        </ProgressIndicatorContainer>
      </ScrollableTableContainer>
    </>
  );
}

RoleMembersSection.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.number,
    owners: PropTypes.array,
    name: PropTypes.string,
    entitlements: PropTypes.array,
    users: PropTypes.array,
    builtin: PropTypes.bool
  }),
  updateRole: PropTypes.func,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      username: PropTypes.string,
      email: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string
    })
  ),
  canEditRoleUsers: PropTypes.bool,
  isSaved: PropTypes.bool,
  error: PropTypes.bool,
  loading: PropTypes.bool
};
